import {
  faClone,
  faDownload,
  faSpinner,
} from '@fortawesome/free-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { useExportExportDeclinedAssignmentsHook } from 'api/export/export';
import type { DeclinedAssignmentDetail } from 'api/model/declinedAssignmentDetail';
import type { DeclinedAssignmentFieldTesterSummary } from 'api/model/declinedAssignmentFieldTesterSummary';
import { useQualityGetDeclinedAssignments } from 'api/quality/quality';
import Button from 'components/inputs/Button';
import themes from 'constants/Colors';
import themeSizes from 'constants/Sizes';
import useTranslations from 'contexts/basicData/useTranslations';
import { getRouteForCaseType } from 'hooks/useCurrentRouteCaseType';
import { useMemo, useState } from 'react';
import toast from 'react-hot-toast';
import { Link } from 'react-router-dom';
import styled from 'styled-components';
import { useCopyToClipboard } from 'usehooks-ts';
import { showException } from 'utils/exception-helper';
import { downloadBlob } from 'utils/file-utils';
import DateRangeFilter, {
  DateRange,
  endOfDay,
  FilterSelect,
  presetForDays,
  startOfDay,
} from '../DateRangeFilter';

/**
 * Kvalitet (BETA) › Nekade uppdrag.
 *
 * Alla avböjningar fälttestare gjort i appen (CaseEvent AssignmentDeclined) i vald
 * period, med uppdragsdetaljer och anledning, plus en sammanställning per
 * fälttestare i relation till accepterade tilldelningar. Underlaget skickas vidare
 * till gruppledare, därför finns Excel-export och "kopiera som text".
 *
 * Filtren på fälttestare/grupp tillämpas i klienten (perioden hämtas ofiltrerad så
 * att rullistorna alltid visar alla som nekat) och skickas med till exporten så att
 * filen speglar det som visas.
 */

const PageContainer = styled.div`
  padding: 20px;
  display: flex;
  flex-direction: column;
  gap: 20px;
`;

const PageTitle = styled.h1`
  font-size: ${themeSizes.font.large};
  margin-bottom: 10px;
`;

const Card = styled.div`
  padding: 15px;
  border: 1px solid ${themes.light.border.primary};
  border-radius: 4px;
  display: flex;
  flex-direction: column;
`;

const CardTitle = styled.h2`
  font-size: ${themeSizes.font.medium};
  margin-bottom: 10px;
  padding-bottom: 5px;
  border-bottom: 1px solid ${themes.light.border.primary};
  display: flex;
  justify-content: space-between;
  align-items: center;
  gap: 10px;
`;

const KPIGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  gap: 15px;
`;

const Tag = styled.span<{ color?: string }>`
  display: inline-block;
  padding: 2px 6px;
  border-radius: 4px;
  font-size: 11px;
  background-color: ${({ color }) => (color ? `${color}20` : '#eee')};
  color: ${({ color }) => color ?? '#333'};
  margin: 1px 2px;
  white-space: nowrap;
`;

const LinkButton = styled.button`
  border: none;
  background: none;
  padding: 0;
  color: #17a2b8;
  cursor: pointer;
  font-size: inherit;
  text-align: left;

  &:hover {
    text-decoration: underline;
  }
`;

const IconButton = styled.button`
  border: 1px solid #ccc;
  background: #fff;
  border-radius: 4px;
  padding: 2px 6px;
  cursor: pointer;
  color: #555;
  font-size: 11px;

  &:hover {
    background: #f1f3f5;
  }
`;

const COLORS = {
  red: '#dc3545',
  blue: '#17a2b8',
  orange: '#fd7e14',
  gray: '#6c757d',
};

const StatBox = ({
  label,
  value,
  sub,
  color,
}: {
  label: string;
  value: string;
  sub?: string;
  color: string;
}) => (
  <div
    style={{
      textAlign: 'center',
      padding: '15px',
      backgroundColor: `${color}15`,
      borderRadius: '8px',
      border: `2px solid ${color}`,
    }}
  >
    <div style={{ fontSize: '28px', fontWeight: 'bold', color }}>{value}</div>
    <div style={{ fontSize: '14px', fontWeight: '500', marginTop: '5px' }}>
      {label}
    </div>
    {sub && (
      <div style={{ fontSize: '12px', color: '#666', marginTop: '2px' }}>
        {sub}
      </div>
    )}
  </div>
);

const thStyle: React.CSSProperties = {
  textAlign: 'left',
  padding: '8px',
  borderBottom: '2px solid #ddd',
  position: 'sticky',
  top: 0,
  backgroundColor: '#fff',
  fontSize: '12px',
  whiteSpace: 'nowrap',
};

const sortableThStyle: React.CSSProperties = {
  ...thStyle,
  cursor: 'pointer',
  userSelect: 'none',
};

const tdStyle: React.CSSProperties = {
  padding: '6px 8px',
  fontSize: '13px',
  verticalAlign: 'top',
};

const STATUS_LABELS: Record<string, string> = {
  Created: 'Skapad',
  OnHold: 'Pausad',
  Planned: 'Planerad',
  Assigned: 'Tilldelad',
  Accepted: 'Accepterad',
  Started: 'Påbörjad',
  Arrived: 'Anlänt',
  Returning: 'På väg tillbaka',
  Complete: 'Slutförd',
  Approved: 'Godkänd',
  Canceled: 'Avbokad',
  Aborted: 'Avbruten',
  Discarded: 'Borttagen',
};

const statusLabel = (status: string): string => STATUS_LABELS[status] ?? status;

const formatDateTime = (date: Date): string =>
  date.toLocaleString('sv-SE', {
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
    hour: '2-digit',
    minute: '2-digit',
  });

const formatHours = (hours: number): string =>
  `${hours.toLocaleString('sv-SE', { maximumFractionDigits: 1 })} h`;

const formatEstimatedHours = (ms?: number | null): string =>
  ms == null
    ? '-'
    : `${(ms / 3_600_000).toLocaleString('sv-SE', {
        maximumFractionDigits: 1,
      })} h`;

const formatAddress = (address: string, zip: string, city: string): string =>
  [address, `${zip} ${city}`.trim()]
    .filter((p) => p.trim().length > 0)
    .join(', ');

const formatShare = (share: number): string =>
  `${(share * 100).toLocaleString('sv-SE', { maximumFractionDigits: 1 })} %`;

const bookedStart = (d: DeclinedAssignmentDetail): Date =>
  d.estimatedStartTime ?? d.bookedTo;

const caseUrl = (d: DeclinedAssignmentDetail): string =>
  `${getRouteForCaseType(d.caseTypeId)}/${d.caseId}`;

// Tabbar/radbrytningar ersätts så att ett värde aldrig spränger sin cell när texten
// klistras in som tabbseparerade värden i Excel eller ett mail.
const sanitizeCell = (value: string): string =>
  value.replace(/\r?\n/g, '; ').replace(/\t/g, ' ');

const COPY_HEADERS = [
  'Fälttestare',
  'Grupp',
  'Gruppledare',
  'Nekad',
  'Timmar före start',
  'Uppdrag',
  'Ärendetyp',
  'Regnr',
  'Bokad start',
  'Estimerad tid',
  'Från',
  'Till',
  'Anledning',
  'Nuvarande status',
  'Nuvarande fälttestare',
];

const buildRowTsv = (
  d: DeclinedAssignmentDetail,
  caseTypeName: string
): string =>
  [
    d.fieldTesterName,
    d.teamName ?? '',
    d.groupLeaderName ?? '',
    formatDateTime(d.declinedAt),
    formatHours(d.hoursBeforeStart),
    String(d.assignmentId),
    caseTypeName,
    d.registrationNumber,
    formatDateTime(bookedStart(d)),
    formatEstimatedHours(d.estimatedDurationMs),
    formatAddress(d.fromAddress, d.fromZip, d.fromCity),
    formatAddress(d.toAddress, d.toZip, d.toCity),
    d.reason,
    statusLabel(d.currentStatus),
    d.currentFieldTesterName ?? '',
  ]
    .map((v) => sanitizeCell(v ?? ''))
    .join('\t');

type SummarySortField =
  | 'fieldTesterName'
  | 'teamName'
  | 'declineCount'
  | 'acceptedCount'
  | 'declineShare'
  | 'lastDeclinedAt';
type SortDirection = 'asc' | 'desc';

const compareSummary = (
  a: DeclinedAssignmentFieldTesterSummary,
  b: DeclinedAssignmentFieldTesterSummary,
  field: SummarySortField
): number => {
  switch (field) {
    case 'fieldTesterName':
      return a.fieldTesterName.localeCompare(b.fieldTesterName, 'sv');
    case 'teamName':
      return (a.teamName ?? '').localeCompare(b.teamName ?? '', 'sv');
    case 'declineCount':
      return a.declineCount - b.declineCount;
    case 'acceptedCount':
      return a.acceptedCount - b.acceptedCount;
    case 'declineShare':
      return a.declineShare - b.declineShare;
    case 'lastDeclinedAt':
    default:
      return a.lastDeclinedAt.getTime() - b.lastDeclinedAt.getTime();
  }
};

const SortIndicator = ({
  field,
  sortField,
  sortDirection,
}: {
  field: SummarySortField;
  sortField: SummarySortField;
  sortDirection: SortDirection;
}) => {
  if (sortField !== field) return null;
  return <span>{sortDirection === 'asc' ? ' ▲' : ' ▼'}</span>;
};

const MIN_DECLINE_OPTIONS = [1, 2, 3, 5];

const DeclinedAssignmentsPage = () => {
  const [range, setRange] = useState<DateRange>(() => presetForDays(30));
  const [fieldTesterFilter, setFieldTesterFilter] = useState<number | 'all'>(
    'all'
  );
  const [teamFilter, setTeamFilter] = useState<number | 'all'>('all');
  const [minDeclines, setMinDeclines] = useState<number>(1);
  const [sortField, setSortField] = useState<SummarySortField>('declineCount');
  const [sortDirection, setSortDirection] = useState<SortDirection>('desc');
  const [isDownloading, setIsDownloading] = useState(false);
  const [, copy] = useCopyToClipboard();

  const { caseTypes } = useTranslations();
  const caseTypeName = (caseTypeId: number): string =>
    caseTypes[caseTypeId] ?? String(caseTypeId);

  const queryParams = useMemo(
    () => ({
      from: startOfDay(range.fromYmd),
      to: endOfDay(range.toYmd),
    }),
    [range]
  );

  const { data, isLoading } = useQualityGetDeclinedAssignments(queryParams);
  const downloadExcel = useExportExportDeclinedAssignmentsHook();

  const handleSort = (field: SummarySortField) => {
    if (sortField === field) {
      setSortDirection(sortDirection === 'asc' ? 'desc' : 'asc');
    } else {
      setSortField(field);
      setSortDirection(field === 'fieldTesterName' ? 'asc' : 'desc');
    }
  };

  const copyText = async (text: string, what: string) => {
    const ok = await copy(text);
    if (ok) {
      toast.success(`${what} kopierad till urklipp`);
    } else {
      toast.error('Kunde inte kopiera till urklipp');
    }
  };

  const handleDownload = async () => {
    try {
      setIsDownloading(true);
      const result = await downloadExcel({
        startDate: queryParams.from,
        endDate: queryParams.to,
        fieldTesterIds:
          fieldTesterFilter === 'all' ? undefined : [fieldTesterFilter],
        teamIds: teamFilter === 'all' ? undefined : [teamFilter],
      });
      if (result) {
        downloadBlob(
          result,
          `Kvalitet_NekadeUppdrag_${range.fromYmd}_${range.toYmd}.xlsx`
        );
      }
    } catch (error) {
      showException(error);
    } finally {
      setIsDownloading(false);
    }
  };

  if (isLoading || !data) {
    return (
      <PageContainer>
        <PageTitle>Nekade uppdrag</PageTitle>
        <DateRangeFilter range={range} onChange={setRange} />
        <div style={{ textAlign: 'center', padding: '60px' }}>
          <FontAwesomeIcon icon={faSpinner} spin size="2x" />
          <p style={{ marginTop: '10px' }}>Laddar nekade uppdrag...</p>
        </div>
      </PageContainer>
    );
  }

  const { summary, declines } = data;

  // Rullistorna byggs från allt i perioden, så en vald fälttestare/grupp inte
  // försvinner ur listan när filtret slår till.
  const fieldTesterOptions = [...summary].sort((a, b) =>
    a.fieldTesterName.localeCompare(b.fieldTesterName, 'sv')
  );
  const teamOptions = Array.from(
    new Map<number, string>(
      summary
        .filter((s) => s.teamId != null && s.teamName)
        .map((s): [number, string] => [
          s.teamId as number,
          s.teamName as string,
        ])
    ).entries()
  ).sort((a, b) => a[1].localeCompare(b[1], 'sv'));

  const matchesFilters = (fieldTesterId: number, teamId?: number | null) =>
    (fieldTesterFilter === 'all' || fieldTesterId === fieldTesterFilter) &&
    (teamFilter === 'all' || teamId === teamFilter);

  const visibleSummary = summary
    .filter(
      (s) =>
        matchesFilters(s.fieldTesterId, s.teamId) &&
        s.declineCount >= minDeclines
    )
    .sort((a, b) => {
      const cmp = compareSummary(a, b, sortField);
      return sortDirection === 'asc' ? cmp : -cmp;
    });
  const visibleTesterIds = new Set(visibleSummary.map((s) => s.fieldTesterId));
  const visibleDeclines = declines.filter((d) =>
    visibleTesterIds.has(d.fieldTesterId)
  );

  const visibleDeclineCount = visibleSummary.reduce(
    (sum, s) => sum + s.declineCount,
    0
  );
  const visibleAcceptedCount = visibleSummary.reduce(
    (sum, s) => sum + s.acceptedCount,
    0
  );
  const visibleShare =
    visibleDeclineCount + visibleAcceptedCount > 0
      ? visibleDeclineCount / (visibleDeclineCount + visibleAcceptedCount)
      : 0;

  const copyAllVisible = () =>
    copyText(
      [
        COPY_HEADERS.join('\t'),
        ...visibleDeclines.map((d) =>
          buildRowTsv(d, caseTypeName(d.caseTypeId))
        ),
      ].join('\n'),
      `${visibleDeclines.length} rader`
    );

  const sortableHeader = (label: string, field: SummarySortField) => (
    <th key={field} style={sortableThStyle} onClick={() => handleSort(field)}>
      {label}
      <SortIndicator
        field={field}
        sortField={sortField}
        sortDirection={sortDirection}
      />
    </th>
  );

  return (
    <PageContainer>
      <PageTitle>Nekade uppdrag</PageTitle>
      <p style={{ fontSize: '13px', color: '#555', margin: 0 }}>
        Uppdrag som fälttestare avböjt via Avböj-knappen i appen. Fälttestaren
        är den som tryckte Avböj, även om uppdraget sedan planerats om. Andelen
        räknas mot fälttestarens accepterade tilldelningar i samma period.
      </p>
      <DateRangeFilter range={range} onChange={setRange}>
        <span style={{ marginLeft: '12px', fontSize: '13px', color: '#666' }}>
          Fälttestare:
        </span>
        <FilterSelect
          value={String(fieldTesterFilter)}
          onChange={(e) =>
            setFieldTesterFilter(
              e.target.value === 'all' ? 'all' : Number(e.target.value)
            )
          }
        >
          <option value="all">Alla</option>
          {fieldTesterOptions.map((s) => (
            <option key={s.fieldTesterId} value={s.fieldTesterId}>
              {s.fieldTesterName}
            </option>
          ))}
        </FilterSelect>
        <span style={{ fontSize: '13px', color: '#666' }}>Grupp:</span>
        <FilterSelect
          value={String(teamFilter)}
          onChange={(e) =>
            setTeamFilter(
              e.target.value === 'all' ? 'all' : Number(e.target.value)
            )
          }
        >
          <option value="all">Alla</option>
          {teamOptions.map(([id, name]) => (
            <option key={id} value={id}>
              {name}
            </option>
          ))}
        </FilterSelect>
        <span style={{ fontSize: '13px', color: '#666' }}>Minst:</span>
        <FilterSelect
          value={String(minDeclines)}
          onChange={(e) => setMinDeclines(Number(e.target.value))}
        >
          {MIN_DECLINE_OPTIONS.map((n) => (
            <option key={n} value={n}>
              {n === 1 ? '1 nekning' : `${n} nekningar`}
            </option>
          ))}
        </FilterSelect>
        <span style={{ marginLeft: 'auto' }}>
          <Button
            type="button"
            onClick={handleDownload}
            disabled={isDownloading || visibleDeclines.length === 0}
          >
            <FontAwesomeIcon icon={faDownload} /> Ladda ner Excel
            {isDownloading ? (
              <FontAwesomeIcon
                icon={faSpinner}
                spin
                style={{ marginLeft: '5px' }}
              />
            ) : null}
          </Button>
        </span>
      </DateRangeFilter>

      <KPIGrid>
        <StatBox
          label="Nekningar i perioden"
          value={String(visibleDeclineCount)}
          sub={
            visibleDeclineCount !== data.totalDeclines
              ? `av ${data.totalDeclines} totalt`
              : undefined
          }
          color={COLORS.red}
        />
        <StatBox
          label="Fälttestare som nekat"
          value={String(visibleSummary.length)}
          sub={
            visibleSummary.length !== summary.length
              ? `av ${summary.length} totalt`
              : undefined
          }
          color={COLORS.orange}
        />
        <StatBox
          label="Andel av besvarade tilldelningar"
          value={formatShare(visibleShare)}
          sub={`${visibleDeclineCount} nekade / ${visibleAcceptedCount} accepterade`}
          color={COLORS.blue}
        />
      </KPIGrid>

      <Card>
        <CardTitle>
          <span>Per fälttestare ({visibleSummary.length})</span>
        </CardTitle>
        {visibleSummary.length === 0 ? (
          <p style={{ color: '#888', textAlign: 'center', padding: '20px' }}>
            Inga nekningar matchar filtren i valt fönster.
          </p>
        ) : (
          <div style={{ overflowY: 'auto', maxHeight: '400px' }}>
            <table style={{ width: '100%', borderCollapse: 'collapse' }}>
              <thead>
                <tr>
                  {sortableHeader('Fälttestare', 'fieldTesterName')}
                  {sortableHeader('Grupp', 'teamName')}
                  <th style={thStyle}>Gruppledare</th>
                  {sortableHeader('Nekningar', 'declineCount')}
                  {sortableHeader('Accepterade', 'acceptedCount')}
                  {sortableHeader('Andel nekade', 'declineShare')}
                  {sortableHeader('Senaste nekning', 'lastDeclinedAt')}
                </tr>
              </thead>
              <tbody>
                {visibleSummary.map((s) => (
                  <tr key={s.fieldTesterId}>
                    <td style={tdStyle}>
                      <LinkButton
                        type="button"
                        title="Visa bara den här fälttestaren"
                        onClick={() =>
                          setFieldTesterFilter(
                            fieldTesterFilter === s.fieldTesterId
                              ? 'all'
                              : s.fieldTesterId
                          )
                        }
                      >
                        {s.fieldTesterName}
                      </LinkButton>
                    </td>
                    <td style={tdStyle}>{s.teamName ?? '-'}</td>
                    <td style={tdStyle}>{s.groupLeaderName ?? '-'}</td>
                    <td style={{ ...tdStyle, fontWeight: 'bold' }}>
                      {s.declineCount}
                    </td>
                    <td style={tdStyle}>{s.acceptedCount}</td>
                    <td style={tdStyle}>
                      <Tag
                        color={s.declineShare >= 0.2 ? COLORS.red : COLORS.gray}
                      >
                        {formatShare(s.declineShare)}
                      </Tag>
                    </td>
                    <td style={{ ...tdStyle, whiteSpace: 'nowrap' }}>
                      {formatDateTime(s.lastDeclinedAt)}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </Card>

      <Card>
        <CardTitle>
          <span>
            Nekningar ({visibleDeclines.length} av {data.totalDeclines})
          </span>
          <IconButton
            type="button"
            onClick={copyAllVisible}
            disabled={visibleDeclines.length === 0}
            title="Kopiera alla synliga rader som tabbseparerad text"
          >
            <FontAwesomeIcon icon={faClone} /> Kopiera alla rader
          </IconButton>
        </CardTitle>
        {visibleDeclines.length === 0 ? (
          <p style={{ color: '#888', textAlign: 'center', padding: '20px' }}>
            Inga nekningar matchar filtren i valt fönster.
          </p>
        ) : (
          <div style={{ overflowY: 'auto', maxHeight: '700px' }}>
            <table style={{ width: '100%', borderCollapse: 'collapse' }}>
              <thead>
                <tr>
                  {[
                    'Nekad',
                    'Fälttestare',
                    'Grupp',
                    'Uppdrag',
                    'Ärendetyp',
                    'Regnr',
                    'Bokad start',
                    'Före start',
                    'Est. tid',
                    'Från',
                    'Till',
                    'Anledning',
                    'Nu',
                    '',
                  ].map((h, i) => (
                    <th key={`${h}-${i}`} style={thStyle}>
                      {h}
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {visibleDeclines.map((d) => (
                  <tr key={d.eventId}>
                    <td style={{ ...tdStyle, whiteSpace: 'nowrap' }}>
                      {formatDateTime(d.declinedAt)}
                    </td>
                    <td style={tdStyle}>
                      {d.fieldTesterName}
                      {d.groupLeaderName && (
                        <div style={{ fontSize: '11px', color: '#888' }}>
                          GL: {d.groupLeaderName}
                        </div>
                      )}
                    </td>
                    <td style={tdStyle}>{d.teamName ?? '-'}</td>
                    <td style={tdStyle}>
                      <Link
                        to={caseUrl(d)}
                        style={{ color: COLORS.blue, textDecoration: 'none' }}
                      >
                        {d.assignmentId}
                      </Link>
                    </td>
                    <td style={tdStyle}>{caseTypeName(d.caseTypeId)}</td>
                    <td style={{ ...tdStyle, whiteSpace: 'nowrap' }}>
                      {d.registrationNumber || '-'}
                    </td>
                    <td style={{ ...tdStyle, whiteSpace: 'nowrap' }}>
                      {formatDateTime(bookedStart(d))}
                    </td>
                    <td style={{ ...tdStyle, whiteSpace: 'nowrap' }}>
                      {d.hoursBeforeStart < 0 ? (
                        <Tag color={COLORS.red}>
                          {formatHours(-d.hoursBeforeStart)} efter start
                        </Tag>
                      ) : (
                        formatHours(d.hoursBeforeStart)
                      )}
                    </td>
                    <td style={{ ...tdStyle, whiteSpace: 'nowrap' }}>
                      {formatEstimatedHours(d.estimatedDurationMs)}
                    </td>
                    <td style={{ ...tdStyle, fontSize: '12px' }}>
                      {formatAddress(d.fromAddress, d.fromZip, d.fromCity) ||
                        '-'}
                    </td>
                    <td style={{ ...tdStyle, fontSize: '12px' }}>
                      {formatAddress(d.toAddress, d.toZip, d.toCity) || '-'}
                    </td>
                    <td
                      style={{
                        ...tdStyle,
                        fontSize: '12px',
                        minWidth: '180px',
                        whiteSpace: 'pre-wrap',
                      }}
                    >
                      {d.reason ? (
                        d.reason
                      ) : (
                        <span style={{ color: '#999', fontStyle: 'italic' }}>
                          Ingen anledning angiven
                        </span>
                      )}
                    </td>
                    <td style={{ ...tdStyle, fontSize: '12px' }}>
                      {statusLabel(d.currentStatus)}
                      {d.currentFieldTesterName && (
                        <div style={{ color: '#888' }}>
                          {d.currentFieldTesterName}
                        </div>
                      )}
                    </td>
                    <td style={tdStyle}>
                      <IconButton
                        type="button"
                        title="Kopiera raden som tabbseparerad text"
                        onClick={() =>
                          copyText(
                            buildRowTsv(d, caseTypeName(d.caseTypeId)),
                            'Rad'
                          )
                        }
                      >
                        <FontAwesomeIcon icon={faClone} />
                      </IconButton>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </Card>
    </PageContainer>
  );
};

export default DeclinedAssignmentsPage;
